import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'

import { Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material'

import {
    OrganizationSectionTimesType,
    organizationSectionTimesSchema,
} from '@/api/organizations'
import {
    fetchOrganizationSectionTimesQueryOptions,
    useUpdateOrganizationSectionTimesMutation,
} from '@/api/organizationsQueryOptions'
import LoadProgress from '@/components/LoadProgress'
import ScoregTimePicker from '@/components/mui/ScoregTimePicker'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'

import SelectWeekday from '@/components/select/SelectWeekday'

export const Route = createFileRoute(
    '/_auth/organization_/$organizationUuid/sectionTimes',
)({
    component: OrganizationComponent,
})

function OrganizationComponent() {
    const params = Route.useParams()
    const organizationQuery = useSuspenseQuery(
        fetchOrganizationSectionTimesQueryOptions(params.organizationUuid),
    )
    const organization = organizationQuery.data

    const updateOrganizationMutation = useUpdateOrganizationSectionTimesMutation(
        params.organizationUuid,
    )

    const {
        register,
        control,
        handleSubmit,
        formState: { isValid, errors },
    } = useForm<OrganizationSectionTimesType>({
        resolver: yupResolver<OrganizationSectionTimesType, any, any>(
            organizationSectionTimesSchema,
        ),
        mode: 'onBlur',
        shouldFocusError: true,
    })

    const onSubmit = (data: OrganizationSectionTimesType) => {
        if (isValid) {
            updateOrganizationMutation.mutate({ ...organization, ...data })
            enqueueSnackbar(t('action.changed', { entity: t('entity.organization') }))
        } else {
            console.log(errors)
        }
    }

    const onError = () => {
        console.log('error')
    }

    if (
        organizationQuery.isLoading ||
        organizationQuery.isFetching ||
        !organization
    ) {
        return <LoadProgress />
    }

    const writeable = organization.permissions
        ? organization.permissions.some((perm) => perm === 'write')
        : false

    return (
        (<form onBlur={handleSubmit(onSubmit, onError)}>
            <Grid container spacing={1}>
                <Grid
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 8
                    }}>
                    <input
                        type="hidden"
                        {...register('uuid')}
                        defaultValue={organization.uuid}
                    />

                    <div>
                        <Grid container spacing={1} sx={{ paddingTop: 4 }}>
                            <Grid size={12}>
                                <Typography variant="subtitle1">Biber</Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <ScoregTimePicker
                                    control={control}
                                    name="biStartTime"
                                    defaultValue={
                                        organization.biStartTime
                                            ? dayjs(organization.biStartTime, 'hh:mm:ss')
                                            : undefined
                                    }
                                    label={t('property.organization.sectionStartTime')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <ScoregTimePicker
                                    control={control}
                                    name="biEndTime"
                                    defaultValue={
                                        organization.biEndTime
                                            ? dayjs(organization.biEndTime, 'hh:mm:ss')
                                            : undefined
                                    }
                                    label={t('property.organization.sectionEndTime')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <SelectWeekday
                                    control={control}
                                    name="biWeekday"
                                    defaultValue={organization.biWeekday || ''}
                                    label={t('property.organization.sectionWeekday')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <TextField
                                    defaultValue={organization.biComment || ''}
                                    {...register('biComment')}
                                    disabled={!writeable}
                                    required={true}
                                    label={t('property.organization.sectionComment')}
                                    helperText={errors.biComment?.message}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} sx={{ paddingTop: 4 }}>
                            <Grid size={12}>
                                <Typography variant="subtitle1">Wichtel & Wölflinge</Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <ScoregTimePicker
                                    control={control}
                                    name="wiwoStartTime"
                                    defaultValue={
                                        organization.wiwoStartTime
                                            ? dayjs(organization.wiwoStartTime, 'hh:mm:ss')
                                            : undefined
                                    }
                                    label={t('property.organization.sectionStartTime')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <ScoregTimePicker
                                    control={control}
                                    name="wiwoEndTime"
                                    defaultValue={
                                        organization.wiwoEndTime
                                            ? dayjs(organization.wiwoEndTime, 'hh:mm:ss')
                                            : undefined
                                    }
                                    label={t('property.organization.sectionEndTime')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <SelectWeekday
                                    control={control}
                                    name="wiwoWeekday"
                                    defaultValue={organization.wiwoWeekday || ''}
                                    label={t('property.organization.sectionWeekday')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <TextField
                                    defaultValue={organization.wiwoComment || ''}
                                    {...register('wiwoComment')}
                                    disabled={!writeable}
                                    required={true}
                                    label={t('property.organization.sectionComment')}
                                    helperText={errors.wiwoComment?.message}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} sx={{ paddingTop: 4 }}>
                            <Grid size={12}>
                                <Typography variant="subtitle1">Guides & Späher</Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <ScoregTimePicker
                                    control={control}
                                    name="guspStartTime"
                                    defaultValue={
                                        organization.guspStartTime
                                            ? dayjs(organization.guspStartTime, 'hh:mm:ss')
                                            : undefined
                                    }
                                    label={t('property.organization.sectionStartTime')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <ScoregTimePicker
                                    control={control}
                                    name="guspEndTime"
                                    defaultValue={
                                        organization.guspEndTime
                                            ? dayjs(organization.guspEndTime, 'hh:mm:ss')
                                            : undefined
                                    }
                                    label={t('property.organization.sectionEndTime')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <SelectWeekday
                                    control={control}
                                    name="guspWeekday"
                                    defaultValue={organization.guspWeekday || ''}
                                    label={t('property.organization.sectionWeekday')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <TextField
                                    defaultValue={organization.guspComment || ''}
                                    {...register('guspComment')}
                                    disabled={!writeable}
                                    required={true}
                                    label={t('property.organization.sectionComment')}
                                    helperText={errors.guspComment?.message}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} sx={{ paddingTop: 4 }}>
                            <Grid size={12}>
                                <Typography variant="subtitle1">
                                    Caravelles & Explorer
                                </Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <ScoregTimePicker
                                    control={control}
                                    name="caexStartTime"
                                    defaultValue={
                                        organization.caexStartTime
                                            ? dayjs(organization.caexStartTime, 'hh:mm:ss')
                                            : undefined
                                    }
                                    label={t('property.organization.sectionStartTime')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <ScoregTimePicker
                                    control={control}
                                    name="caexEndTime"
                                    defaultValue={
                                        organization.caexEndTime
                                            ? dayjs(organization.caexEndTime, 'hh:mm:ss')
                                            : undefined
                                    }
                                    label={t('property.organization.sectionEndTime')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <SelectWeekday
                                    control={control}
                                    name="caexWeekday"
                                    defaultValue={organization.caexWeekday || ''}
                                    label={t('property.organization.sectionWeekday')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <TextField
                                    defaultValue={organization.caexComment || ''}
                                    {...register('caexComment')}
                                    disabled={!writeable}
                                    required={true}
                                    label={t('property.organization.sectionComment')}
                                    helperText={errors.caexComment?.message}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={1} sx={{ paddingTop: 4 }}>
                            <Grid size={12}>
                                <Typography variant="subtitle1">Ranger & Rover</Typography>
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <ScoregTimePicker
                                    control={control}
                                    name="raroStartTime"
                                    defaultValue={
                                        organization.raroStartTime
                                            ? dayjs(organization.raroStartTime, 'hh:mm:ss')
                                            : undefined
                                    }
                                    label={t('property.organization.sectionStartTime')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <ScoregTimePicker
                                    control={control}
                                    name="raroEndTime"
                                    defaultValue={
                                        organization.raroEndTime
                                            ? dayjs(organization.raroEndTime, 'hh:mm:ss')
                                            : undefined
                                    }
                                    label={t('property.organization.sectionEndTime')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                    md: 4,
                                    lg: 2
                                }}>
                                <SelectWeekday
                                    control={control}
                                    name="raroWeekday"
                                    defaultValue={organization.raroWeekday || ''}
                                    label={t('property.organization.sectionWeekday')}
                                    disabled={!writeable}
                                />
                            </Grid>
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 6
                                }}>
                                <TextField
                                    defaultValue={organization.raroComment || ''}
                                    {...register('raroComment')}
                                    disabled={!writeable}
                                    required={true}
                                    label={t('property.organization.sectionComment')}
                                    helperText={errors.raroComment?.message}
                                />
                            </Grid>
                        </Grid>
                        <Grid>
                            <FormControlLabel control={
                                <Checkbox {...register("publishTimes")} />}
                                label={t('property.organization.publishTimes')} />
                        </Grid>
                    </div>
                </Grid>
            </Grid>
        </form>)
    );
}
